import { Tag, Button, Upload, Tooltip } from "antd";
import React from "react";
import { FileOutlined, FileTextOutlined, PrinterOutlined, UploadOutlined, AlertOutlined, RightCircleOutlined } from '@ant-design/icons';
import { formatAgeShort, ConvertStringToDate, getUserDetails } from "../../utils/helper";
import { StudyDateTime } from "../../components/StudyDateTime";
import { FieldLabels, getPacsStatusLabel, getPatientIdLabel } from "../../constants/fieldLabels";
import { getStatusStyle } from "../../utils/sharedTableColumns";
import { ViewerIcon, SeriesIcon, ACTION_HOVER } from "../../components/ActionIcons";

const iconStyle = {
  fontSize: '16px',
  cursor: 'pointer',
  color: '#4A4A4A',
};

export const orderColumns = ({ openReportEditor, role, addFile, viewNotes, printReport, captureReceiver, isConsultant, openViewer, viewReport, selectSeries, showPatientOrders }) => {
  const userDetails = getUserDetails();

  return ([
    {
      dataIndex: "patient.pat_name",
      title: FieldLabels.PATIENT_NAME,
      render: (text, record) => {
        const isEmergency = record.po_priority?.toUpperCase() === 'EMERGENCY';
        const patientName = record.patient?.pat_name;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Button
              className="ms-1auto d-flex align-items-center ph-0"
              type="link"
              style={{ padding: 0, minWidth: 0, flex: '0 1 auto', overflow: 'hidden' }}
            >
              <Tooltip title={patientName}>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  maxWidth: isEmergency ? 'calc(100% - 22px)' : '100%'
                }}>
                  {record.patient?.pat_name}
                </span>
              </Tooltip>
              {/* Emergency Icon */}
              {isEmergency && (
                <Tooltip title="Emergency Case">
                  <AlertOutlined
                    style={{
                      color: '#FF4D4F',
                      marginLeft: '6px',
                      fontSize: '14px',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  />
                </Tooltip>
              )}
            </Button>
            {/* Consultant: open-viewer eye icon sits inline after the name */}
            {openViewer && (
              <Tooltip title="Open Viewer">
                <span
                  className="action-icon-button"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1px',
                    width: '22px',
                    height: '22px',
                    flex: '0 0 auto',
                    cursor: 'pointer',
                    ['--ai-hover' as any]: ACTION_HOVER.viewer,
                  }}
                  onClick={(e) => { e.stopPropagation(); openViewer(record); }}
                >
                  <ViewerIcon size={20} />
                </span>
              </Tooltip>
            )}
            {/* Select Series — opens the study's series list, then the viewer filtered
                to the picked series. Shown to users with study-viewing permission. */}
            {selectSeries && (
              <Tooltip title="Select Series">
                <span
                  className="action-icon-button"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1px',
                    width: '22px',
                    height: '22px',
                    flex: '0 0 auto',
                    cursor: 'pointer',
                    ['--ai-hover' as any]: ACTION_HOVER.series,
                  }}
                  onClick={(e) => { e.stopPropagation(); selectSeries(record); }}
                >
                  <SeriesIcon size={20} />
                </span>
              </Tooltip>
            )}
            {/* Prior orders for this patient — same button the PACS table shows,
                only rendered when the patient actually has other orders. */}
            {showPatientOrders && record?.has_prior_orders && (
              <Tooltip title="View Prior Orders">
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); showPatientOrders(record); }}
                  style={{
                    flexShrink: 0,
                    borderRadius: '4px',
                    boxShadow: 'none',
                  }}
                >
                  <RightCircleOutlined />
                </Button>
              </Tooltip>
            )}
          </div>
        )
      },
      width: 250
    },
    {
      dataIndex: "patient.pat_pin",
      title: getPatientIdLabel(),
      width: 105,
      render: (text, rec) => {
        const patPin = rec?.patient?.pat_pin
        return (
          <Tooltip title={patPin}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {patPin}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "patient.pat_age",
      title: FieldLabels.PATIENT_AGE,
      width: 100,
      render: (val, record) => formatAgeShort(record.patient?.pat_dob, record.patient?.pat_age)
    },
    {
      dataIndex: "patient.pat_sex",
      title: FieldLabels.PATIENT_SEX,
      width: 100,
      render: (text, rec) => rec?.patient?.pat_sex
    },
    {
      title: FieldLabels.BODY_PART,
      dataIndex: "po_body_part",
      render: (text) => <div style={{ minWidth: '100px' }}>{text || '-'}</div>,
      width: 120
    },
    {
      dataIndex: "po_diag_desc",
      title: FieldLabels.STUDY_DESCRIPTION,
      width: 200,
      render: (txt, rec) => {
        const diagDesc = rec?.po_diag_desc;
        const priority = rec?.po_priority;
        return (
          <Tooltip title={`${diagDesc}${priority ? ` (Priority: ${priority})` : ''}`}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {diagDesc}
            </div>
          </Tooltip>
        )
      },
    },
        {
      dataIndex: "po_site",
      title: FieldLabels.SITE,
      width: 150,
      render: (text, rec) => {
        // Same source/fallback chain as the PACS table's Site column — never a
        // hardcoded centre, which was wrong on every non-Somajiguda row.
        const site = rec?.po_site || rec?.po_center_name || '';
        return (
          <Tooltip title={site}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: site ? 'left' : 'center'
            }}>
              {site || '-'}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "ps_sending_aet",
      title: FieldLabels.AE_TITLE,
      width: 120,
      render: (text, rec) => {
        const aet = rec?.pacs_study?.ps_sending_aet ?? rec?.ps_sending_aet;
        return (
          <Tooltip title={aet}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: aet ? 'left' : 'center'
            }}>
              {aet || '-'}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "po_modality",
      title: FieldLabels.MODALITY,
      width: 100,
      render: (text, rec) => {
        const display = rec?.po_modality;
        return (
          <span
            style={{
              background: '#F5F5F5',
              border: '1px solid #EAEEF2',
              height: '24px',
              borderRadius: '4px',
              padding: '2px 8px',
              display: 'inline-block',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            }}
          >
            {display}
          </span>
        );
      },
    },
    {
      dataIndex: "po_his_ord_no",
      title: FieldLabels.ORDER_NUMBER,
      width: 100,
      render: (text, rec) => {
        const orderNo = rec?.po_his_ord_no;
        return (
          <Tooltip title={orderNo}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {orderNo}
            </div>
          </Tooltip>
        );
      }
    },
    {
      // Sits right after Order No (same slot as the PACS table) so the Visit ID
      // filter above has a visible counterpart in the grid.
      dataIndex: "po_visit_number",
      title: FieldLabels.VISIT_ID,
      width: 110,
      render: (text, rec) => {
        const visitNo = rec?.po_visit_number;
        return (
          <Tooltip title={visitNo}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: visitNo ? 'left' : 'center'
            }}>
              {visitNo || '-'}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "po_acc_no",
      title: FieldLabels.ACCESSION_NUMBER,
      width: 150,
      render: (text, rec) => {
        const accNo = rec?.po_acc_no;
        return (
          <Tooltip title={accNo}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%'
            }}>
              {accNo}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "po_ref_doc",
      title: FieldLabels.REFERRING_DOCTOR,
      width: 175,
      render: (text, rec) => {
        const refDoc = rec?.po_ref_doc;
        return (
          <Tooltip title={refDoc}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: refDoc ? 'left' : 'center'
            }}>
              {refDoc || '-'}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "ps_study_dt_tm",
      title: FieldLabels.SCAN_DATE,
      render: (val, record) => {
        const dt = record?.pacs_study?.ps_study_dt_tm;
        return dt ? <StudyDateTime value={dt} /> : "-";
      },
      width: 200
    },
    {
      dataIndex: "reportedBy",
      title: FieldLabels.REPORTED_BY,
      width: 150,
      render: (text, rec) => {
        const name = rec?.reportedBy ?? rec?.order_workflow?.ow_reported_by ?? rec?.pr_reported_by ?? '';
        return (
          <Tooltip title={name}>
            <div style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              width: '100%',
              textAlign: name ? 'left' : 'center'
            }}>
              {name || '-'}
            </div>
          </Tooltip>
        );
      }
    },
    {
      dataIndex: "order_workflow.ow_pacs_status",
      title: FieldLabels.STATUS,
      fixed: 'right',
      className: 'first-sticky-column',
      render: (text, record) => {
        const dispatch_status = record?.order_workflow?.ow_pacs_status;
        if (!dispatch_status) return <span>-</span>;
        // Signed-off reports can be opened in the browser's native PDF viewer by
        // clicking the chip — mirrors the PACS table behaviour.
        const clickable = dispatch_status === 'SIGNEDOFF' && !!viewReport;
        return (
          <Tag
            style={{
              ...getStatusStyle(dispatch_status),
              cursor: clickable ? 'pointer' : 'default',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (clickable) viewReport(record);
            }}
          >
            {getPacsStatusLabel(dispatch_status)}
          </Tag>
        );
      },
      // Fits the shared badge's widest label ("UNREPORTED").
      width: 124
    },
    // {
    //   title: FieldLabels.ACTIONS,
    //   key: "actions_column",
    //   fixed: 'right',
    //   // Consultant view has no Actions column — the open-viewer eye icon lives
    //   // inline beside the patient name instead.
    //   hidden: isConsultant,
    //   render: (text, record) => {
    //     const canPrintReport = record?.order_workflow?.ow_pacs_status === 'SIGNEDOFF';
    //     return (
    //       <div style={{
    //         display: 'flex',
    //         alignItems: 'center',
    //         gap: '12px',
    //         padding: '4px 6px',
    //       }}>
    //         {/* Print Report - Only if Signed Off */}
    //         <Tooltip title={canPrintReport ? 'Print Report' : 'Report not signed off'}>
    //           <span
    //             style={{ cursor: canPrintReport ? 'pointer' : 'not-allowed', opacity: canPrintReport ? 1 : 0.5 }}
    //             onClick={(e) => { e.stopPropagation(); canPrintReport && printReport(record); }}
    //           >
    //             <PrinterOutlined style={iconStyle} />
    //           </span>
    //         </Tooltip>
    //       </div>
    //     );
    //   },
    //   width: 100
    // }
  ].filter(itm => !itm.hidden))
};
